import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { isRecord, readStoredJson } from '../lib/persistedState';

export type WorkbenchLayout = { inspectorDock: 'left' | 'right'; inspectorVisible: boolean; explorerVisible: boolean; explorerWidth: number; inspectorWidth: number };

export function useWorkbenchLayout(storage: Storage = localStorage) {
  const isMobileViewport = ref(window.matchMedia('(max-width: 760px)').matches);
  const defaults: WorkbenchLayout = { inspectorDock: 'right', inspectorVisible: false, explorerVisible: true, explorerWidth: 264, inspectorWidth: 292 };
  const layout = reactive((() => {
    const saved = readStoredJson(storage, 'chronis.workspace.layout.v2');
    if (!isRecord(saved)) return defaults;
    return {
      inspectorDock: saved.inspectorDock === 'left' || saved.inspectorDock === 'right' ? saved.inspectorDock : defaults.inspectorDock,
      inspectorVisible: typeof saved.inspectorVisible === 'boolean' ? saved.inspectorVisible : defaults.inspectorVisible,
      explorerVisible: typeof saved.explorerVisible === 'boolean' ? saved.explorerVisible : defaults.explorerVisible,
      explorerWidth: typeof saved.explorerWidth === 'number' && Number.isFinite(saved.explorerWidth) ? Math.round(Math.min(480, Math.max(200, saved.explorerWidth))) : defaults.explorerWidth,
      inspectorWidth: typeof saved.inspectorWidth === 'number' && Number.isFinite(saved.inspectorWidth) ? Math.round(Math.min(520, Math.max(240, saved.inspectorWidth))) : defaults.inspectorWidth
    };
  })());
  let panelResize: { panel: 'explorer' | 'inspector'; startX: number; startWidth: number } | null = null;
  function persistLayout() { try { storage.setItem('chronis.workspace.layout.v2', JSON.stringify(layout)); } catch { /* Speicher ist optional. */ } }
  function resizePanel(event: PointerEvent) {
    if (!panelResize) return;
    const direction = panelResize.panel === 'inspector' && layout.inspectorDock === 'right' ? -1 : 1;
    const next = panelResize.startWidth + (event.clientX - panelResize.startX) * direction;
    if (panelResize.panel === 'explorer') layout.explorerWidth = Math.round(Math.min(480, Math.max(200, next)));
    else layout.inspectorWidth = Math.round(Math.min(520, Math.max(240, next)));
  }
  function stopPanelResize() {
    if (!panelResize) return;
    panelResize = null;
    document.body.classList.remove('panel-resizing');
    persistLayout();
    window.removeEventListener('pointermove', resizePanel);
    window.removeEventListener('pointerup', stopPanelResize);
  }
  function startPanelResize(panel: 'explorer' | 'inspector', event: PointerEvent) {
    if (isMobileViewport.value) return;
    event.preventDefault();
    panelResize = { panel, startX: event.clientX, startWidth: panel === 'explorer' ? layout.explorerWidth : layout.inspectorWidth };
    document.body.classList.add('panel-resizing');
    window.addEventListener('pointermove', resizePanel);
    window.addEventListener('pointerup', stopPanelResize);
  }
  function dockInspector(side: 'left' | 'right') { layout.inspectorDock = side; layout.inspectorVisible = true; persistLayout(); }
  function toggleInspector() {
    layout.inspectorVisible = !layout.inspectorVisible;
    if (isMobileViewport.value && layout.inspectorVisible) layout.explorerVisible = false;
    persistLayout();
  }
  function toggleExplorer() {
    layout.explorerVisible = !layout.explorerVisible;
    if (isMobileViewport.value && layout.explorerVisible) layout.inspectorVisible = false;
    persistLayout();
  }
  function closeMobilePanels() { layout.explorerVisible = false; layout.inspectorVisible = false; persistLayout(); }
  function updateMobileViewport() {
    const isMobile = window.matchMedia('(max-width: 760px)').matches;
    const switchedToMobile = isMobile && !isMobileViewport.value;
    isMobileViewport.value = isMobile;
    if (switchedToMobile) closeMobilePanels();
  }
  function startInspectorDrag(event: DragEvent) { event.dataTransfer?.setData('application/x-chronis-panel', 'inspector'); if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'; }
  function dropInspectorDock(side: 'left' | 'right', event: DragEvent) { if (event.dataTransfer?.getData('application/x-chronis-panel') === 'inspector') dockInspector(side); }
  onMounted(() => {
    window.addEventListener('resize', updateMobileViewport);
    if (isMobileViewport.value) closeMobilePanels();
  });
  onBeforeUnmount(() => {
    stopPanelResize();
    window.removeEventListener('resize', updateMobileViewport);
  });
  return { isMobileViewport, layout, startPanelResize, dockInspector, toggleInspector, toggleExplorer, closeMobilePanels, startInspectorDrag, dropInspectorDock };
}
